import type { DeviceRebootResult } from '@/types/api-client/device-tools'

/**
 * Gets a concise label for the reboot status
 *
 * @param result - The normalized reboot result object
 * @param t - The i18n translation function
 * @returns Localized label text for the status (e.g., "Success", "Failed", "Waiting")
 *
 * @example
 * const label = getRebootLabel(rebootResult.value, t) // "Success"
 */
export function getRebootLabel(
	result: DeviceRebootResult | null,
	t: (key: string) => string
): string {
	if (!result) return ''

	if (result.success) {
		return t('device-tools.reboot.success')
	}
	if (result.hasError) {
		return t('device-tools.reboot.failed')
	}
	return t('device-tools.reboot.waiting')
}

/**
 * Gets a human-readable description for the reboot status
 *
 * @param result - The normalized reboot result object
 * @param t - The i18n translation function
 * @returns Localized description text for the reboot status
 *
 * @example
 * const description = getRebootDescription(rebootResult.value, t) // "Device is rebooting"
 */
export function getRebootDescription(
	result: DeviceRebootResult | null,
	t: (key: string) => string
): string {
	if (!result) return ''

	if (result.isLoading) {
		return t('device-tools.reboot.in-progress.sending')
	}
	if (result.success) {
		return t('device-tools.reboot.device-rebooting')
	}
	if (result.hasError) {
		return t('device-tools.reboot.failed-description')
	}
	return t('device-tools.reboot.description')
}

/**
 * Gets a detailed message for the reboot status
 *
 * @param result - The normalized reboot result object
 * @param t - The i18n translation function
 * @returns Localized message text for the reboot status
 *
 * @example
 * const message = getRebootMessage(rebootResult.value, t) // "Reboot command sent successfully"
 */
export function getRebootMessage(
	result: DeviceRebootResult | null,
	t: (key: string) => string
): string {
	if (!result) return ''

	if (result.isLoading) {
		return t('device-tools.reboot.in-progress.waiting')
	}
	if (result.success) {
		return t('device-tools.reboot.success-message')
	}
	if (result.cooldownSeconds > 0) {
		return t('device-tools.reboot.retry-in-moment')
	}
	if (result.hasError) {
		return t('device-tools.reboot.failed-message')
	}
	return t('device-tools.reboot.ready-status')
}
